/**
 * Durable "subregion-first vs country-first" preference — cookie transport.
 *
 * localStorage stays the source of truth for the shopping location, but SERVER
 * code (API routes, server components, middleware) cannot read it. Waiting for
 * client JS to hand the preference back means the first render is always
 * country-first, which is exactly the render that decides what a shopper sees.
 * This cookie is the server-readable mirror of the one fact the server needs:
 * which country, which admin-1 unit, and which of the two orderings the shopper
 * wants.
 *
 * INERT BY CONSTRUCTION. Absence of the cookie means `country-first`, which is
 * today's behavior, so nothing changes until a later flag reads it and acts.
 *
 * Posture: functional cookie, same as `locale` — 365d, path /, secure, sameSite
 * lax. It carries a stated UI preference, no identifier.
 *
 * EDGE-SAFE ON PURPOSE:
 *  - no `next/headers` import (the cookie store is passed IN, so middleware,
 *    route handlers and RSC can all share this reader);
 *  - no node builtins;
 *  - no admin-1 registry import — 48KB of Natural Earth data has no business in
 *    middleware just to read three fields. Resolving `code` to a canonical unit
 *    is the caller's job, where the registry is already paid for.
 */

/** Which ordering the shopper asked for. Absent everywhere ⇒ `country-first`. */
export type RegionPriority = 'subregion-first' | 'country-first';

/** Whether the shopper stated the region, or we merely inferred it. */
export type RegionPrefSource = 'explicit' | 'detected';

/** Cookie name. Server jobs read this key; do not rename without a migration. */
export const SUBREGION_PREF_COOKIE = 'iai_subregion';

/** Functional-cookie lifetime, in days — matches the `locale` cookie. */
export const SUBREGION_PREF_COOKIE_DAYS = 365;

/**
 * The ordering that reproduces today's behavior. Every absent / malformed /
 * unreadable input resolves to this, so a broken cookie is indistinguishable
 * from no cookie.
 */
export const DEFAULT_REGION_PRIORITY: RegionPriority = 'country-first';

/**
 * Mirror of `ALL_REGIONS_SENTINEL` from
 * `shared/src/region/admin1-registry` — duplicated rather than imported so this
 * module stays free of the 48KB registry. `subregion-pref.test.ts` asserts the
 * two are identical, so the copy cannot drift.
 */
export const WHOLE_COUNTRY_CODE = 'All regions';

/** The three fields the server needs, decoded. */
export interface SubregionPref {
  /** Internal country code, uppercased (the app uses UK where ISO uses GB). */
  country: string;
  /**
   * Admin-1 code as stored ('QC'), a legacy name spelling ('Île-de-France' —
   * France and the UK are keyed by name in the frozen legacy tables), or
   * `WHOLE_COUNTRY_CODE`. Resolve through `getSubregion(country, code)` before
   * comparing.
   */
  code: string;
  priority: RegionPriority;
}

export function isRegionPriority(value: unknown): value is RegionPriority {
  return value === 'subregion-first' || value === 'country-first';
}

export function isRegionPrefSource(value: unknown): value is RegionPrefSource {
  return value === 'explicit' || value === 'detected';
}

/** True when the preference is "the whole country", not a specific unit. */
export function isWholeCountryPref(pref: SubregionPref | null | undefined): boolean {
  const code = pref?.code?.trim();
  return !code || code === WHOLE_COUNTRY_CODE;
}

/**
 * `<CC>:<code>:<priority>` — the raw value, BEFORE any cookie escaping.
 *
 * Hand this straight to `js-cookie`: its writer percent-encodes what a cookie
 * value may not contain (spaces, accents, apostrophes) while leaving `:`
 * literal, so the three segments survive the round trip untouched.
 */
export function encodeSubregionPrefCookie(pref: SubregionPref): string {
  const country = pref.country.trim().toUpperCase();
  const code = pref.code.trim() || WHOLE_COUNTRY_CODE;
  const priority = isRegionPriority(pref.priority) ? pref.priority : DEFAULT_REGION_PRIORITY;
  return `${country}:${code}:${priority}`;
}

/** Percent-decode one segment. Already-decoded and malformed input pass through. */
function decodeSegment(segment: string): string {
  if (!segment.includes('%')) return segment;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Decode a cookie value. TOLERANT: anything unparseable is `null` (⇒ treated as
 * "no preference" ⇒ today's behavior), and this never throws.
 *
 * Accepts the value either percent-encoded (what lands in the Cookie header) or
 * already decoded (some readers decode for you), because which one a caller
 * gets depends on the framework, not on us.
 */
export function parseSubregionPrefCookie(raw: string | null | undefined): SubregionPref | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > 256) return null;

  const parts = trimmed.split(':');
  if (parts.length < 2) return null;

  const country = decodeSegment(parts[0]).trim().toUpperCase();
  // Internal codes are 2 letters ('CA', 'UK'); allow up to 4 for headroom.
  if (!/^[A-Z]{2,4}$/.test(country)) return null;

  // A trailing valid priority is the priority; anything else is part of the
  // code (a legacy name could in principle contain a colon) and the preference
  // falls back to the default ordering.
  const last = decodeSegment(parts[parts.length - 1]).trim();
  const hasPriority = isRegionPriority(last);
  const codeParts = hasPriority ? parts.slice(1, -1) : parts.slice(1);
  const code = decodeSegment(codeParts.join(':')).trim();
  if (!code) return null;

  return {
    country,
    code,
    priority: hasPriority ? (last as RegionPriority) : DEFAULT_REGION_PRIORITY,
  };
}

/**
 * The narrow slice of a cookie store this reader needs. Satisfied by
 * `cookies()` from `next/headers`, by `NextRequest.cookies`, and by a plain
 * object in tests — none of which have to be imported here.
 */
export interface CookieStoreLike {
  get(name: string): { value: string } | undefined;
}

/**
 * Server-side read of the durable preference. `null` means "no stated
 * preference" — which is country-first, i.e. exactly today's behavior.
 *
 * Never throws: a cookie store that is missing, mid-request-error, or holding
 * garbage all resolve to `null`.
 */
export function readSubregionPrefFromCookies(
  cookieStore: CookieStoreLike | null | undefined,
): SubregionPref | null {
  try {
    const raw = cookieStore?.get(SUBREGION_PREF_COOKIE)?.value;
    return parseSubregionPrefCookie(raw);
  } catch {
    return null;
  }
}
